import { Table } from '@vadiun/react-datatable';
import moment, { Moment } from 'moment';
import { DriverIncomeModel } from '../models/DriverIncomeModel';

interface Props {
  incomes: DriverIncomeModel[];
  total: number;
  page: number;
  rowsPerPage: number;
  visibleColumns: {
    average_income_per_day: boolean;
    average_income_per_hour: boolean;
    income_in_period: boolean;
    worked_hours_in_period: boolean;
    shift_open_date_time: boolean;
    total_days_worked: boolean;
    today_income: boolean;
    last_shift_open: boolean;
    today_income_per_worked_hour: boolean;
  };
  onColumnsVisibleChange: (columns: {
    average_income_per_day: boolean;
    average_income_per_hour: boolean;
    income_in_period: boolean;
    worked_hours_in_period: boolean;
    shift_open_date_time: true;
    last_shift_open: boolean;
    total_days_worked: boolean;
    today_income: boolean;
    today_income_per_worked_hour: boolean;
  }) => void;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
  handlePageChange: (currentPage: number) => void;
}

export const DriverIncomeTable = ({
  incomes,
  total,
  page,
  rowsPerPage,
  visibleColumns,
  onColumnsVisibleChange,
  onChangePage,
  onChangeRowsPerPage,
  onSortChange,
  handlePageChange
}: Props) => {
  const columns = [
    {
      name: 'code',
      label: 'Código',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div
              className="b-1 flex text-blue-900 border-b-2 border-t-2 border-blue-200 p-2 justify-center"
              style={{
                background: 'linear-gradient(to bottom, rgb(239, 246, 255), white 60%, rgb(219, 234, 254))'
              }}
            >
              {value}
            </div>
          </div>
        )
      }
    },
    {
      name: 'name',
      label: 'Nombre',
      options: { filter: false, sort: false }
    },
    {
      name: 'shift_open_date_time',
      label: 'Inicio turno',
      options: {
        filter: false,
        sort: false,
        display: visibleColumns.shift_open_date_time,
        customBodyRender: (date: Moment | null) => {
          const [fecha, hora] = date?.format('DD/MM/YYYY HH:mm').split(' ') ?? [];
          return (
            <div className="flex">
              <div
                className="flex gap-4 rounded-2xl p-2"
                style={{ background: 'linear-gradient(to right, rgb(220, 255, 216), white 80%)' }}
              >
                <span className="whitespace-nowrap" style={{ color: 'rgb(0, 41, 4)' }}>{fecha}</span>
                <span className="whitespace-nowrap" style={{ color: 'rgb(0, 140, 12)' }}>{hora}</span>
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'business_unit_name',
      label: 'Unidad de Negocio',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-gray-50 text-gray-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    },
    {
      name: 'today_income',
      label: 'Total - día actual',
      options: {
        display: visibleColumns.today_income,
        filter: false,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-blue-50 text-blue-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    },
    {
      name: 'today_income_per_worked_hour',
      label: 'Por hora - día actual',
      options: {
        display: visibleColumns.today_income_per_worked_hour,
        filter: false,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-orange-50 text-orange-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    },
    {
      name: 'last_shift_open',
      label: 'Fecha de último turno',
      options: {
        display: visibleColumns.last_shift_open,
        filter: false,
        sort: false,
        customBodyRender: (date: Moment | string | null) => {
          if (!date) return null;
          const momentDate = typeof date === 'string' ? moment(date) : date;
          return (
            <div className="flex">
              <div
                className="flex gap-4 rounded-2xl p-2"
                style={{ background: 'linear-gradient(to right, rgb(255, 212, 212), white 80%)' }}
              >
                <span className="whitespace-nowrap" style={{ color: 'rgb(38, 0, 0)' }}>
                  {momentDate.format('DD/MM/YYYY')}
                </span>
                <span className="whitespace-nowrap" style={{ color: 'rgb(156, 0, 0)' }}>
                  {momentDate.format('HH:mm')}
                </span>
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'total_days_worked',
      label: 'Días totales trabajados',
      options: {
        display: visibleColumns.total_days_worked,
        filter: false,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-green-50 text-green-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    },
    {
      name: 'average_income_per_day',
      label: 'Promedio por día',
      options: {
        filter: false,
        display: visibleColumns.average_income_per_day,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-gray-100 text-green-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    },
    {
      name: 'average_income_per_hour',
      label: 'Promedio por hora',
      options: {
        filter: false,
        display: visibleColumns.average_income_per_hour,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-gray-100 text-black-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    },
    {
      name: 'income_in_period',
      label: 'Total en periodo',
      options: {
        filter: false,
        display: visibleColumns.income_in_period,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-gray-100 text-red-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    },
    {
      name: 'worked_hours_in_period',
      label: 'Horas trabajadas en periodo',
      options: {
        filter: false,
        display: visibleColumns.worked_hours_in_period,
        customBodyRender: (value: any) => (
          <div className="flex justify-center">
            <div className="flex bg-green-50 text-green-700 rounded-2xl p-2 justify-center">{value}</div>
          </div>
        )
      }
    }
  ];

  return (
    <div className="w-full">
      <Table
        options={{
          elevation: 0,
          selectableRows: 'none',
          filter: false,
          download: false,
          print: false,
          serverSide: true,
          rowsPerPageOptions: [10, 30, 50, 100, 200, 300, 400, 500],
          count: total,
          page: page - 1,
          rowsPerPage,
          onChangePage: (newPage: number) => {
            onChangePage(newPage + 1);
            handlePageChange(newPage + 1);
          },
          onChangeRowsPerPage: (rpp: number) => onChangeRowsPerPage(rpp),
          onColumnSortChange: (changedColumn: string, direction: 'asc' | 'desc') => {
            onSortChange(direction, changedColumn);
          },
          onViewColumnsChange: (changedColumn: string, action: string) => {
            onColumnsVisibleChange({
              ...visibleColumns,
              [changedColumn]: action === 'add'
            } as any);
          }
        }}
        title={'Choferes'}
        data={incomes}
        columns={columns}
      />
    </div>
  );
};
